import axios from "axios";
import jwt_decode from "jwt-decode";
import { AuthService } from "../services/AuthService";
import { getInArr } from "../helpers/getInArr";
import ErrorHandling from "../services/ErrorHandling";
import {publicUrl, exactPublicUrl} from "@/api/index";
import { handleMaxTwoFactorResponse } from "../helpers/maxTwoFactorApiInterceptor";

let isRefreshing = false; // Флаг для блокировки повторных вызовов
let refreshTokenPromise = null;
let failedQueue = []; // Очередь запросов, которые нужно повторить после обновления токена

const getStoredAccessToken = () => {
  const token = localStorage.getItem("userToken");

  if (!token || token === "undefined" || token === "null") {
    if (token) localStorage.removeItem("userToken");
    return null;
  }

  try {
    const payload = jwt_decode(token);

    if (!payload || typeof payload !== "object") {
      throw new Error("Invalid access token payload");
    }

    return { token, payload };
  } catch (error) {
    // Повреждённый токен не должен останавливать запрос в request interceptor.
    // Удаляем его: защищённый запрос получит 401 и штатно запустит refresh.
    localStorage.removeItem("userToken");
    return null;
  }
};

const refreshToken = () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = AuthService.refreshToken().finally(() => {
      refreshTokenPromise = null;
    });
  }

  return refreshTokenPromise;
};

const getAccessTokenAfterRefresh = async () => {
  await refreshToken();

  const accessToken = getStoredAccessToken();
  if (!accessToken) {
    throw new Error("Refresh response does not contain a valid access token");
  }

  return accessToken.token;
};

const isApiProxyEnabled = process.env.REACT_APP_USE_API_PROXY === "1";
const apiProxyPrefix = process.env.REACT_APP_API_PROXY_PREFIX || "/__api_proxy__/";
const API_BASE_URL = isApiProxyEnabled
  ? apiProxyPrefix
  : process.env.REACT_APP_URL_API;

const getPathFromUrl = (url) => {
  try {
    const urlObj = new URL(url, process.env.REACT_APP_PATH_FILE); // Создаем объект URL
    const path = urlObj.pathname; // Получаем путь (например, "/survey/take-survey")
    return path.startsWith('/') ? path.slice(1) : path; // Убираем начальный слеш
  } catch (e) {
    return url; // Если URL некорректен, возвращаем как есть
  }
};

function removeQueryString(url) {
    if (url.includes('/?')) {
        return url.split('/?')[0];
    }
    if (url.includes('?')) {
        return url.split('?')[0];
    }
    return url;
}

function normalizePath(url) {
    return removeQueryString(getPathFromUrl(url || '')).replace(/\/+$/, '');
}

const isMaxPlatformGateError = (error) => {
  const data = error?.response?.data;
  const code = data?.meta?.code || data?.errorCode;
  const action = data?.meta?.action;

  return error?.response?.status === 423 && (
    code === 'max_binding_required' ||
    code === 'max_confirmation_required' ||
    action === 'open_max_binding_modal' ||
    action === 'open_max_confirmation_modal'
  );
}

class apiCreator {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      mode: "no-cors",
      headers: {
        "Content-Type": "application/json",
        withCredentials: true,
      },
    });
    const api = this.api;

    // Перехватчик ответа от сервера проверка на ошибку авторизации 401
    // Если код ответа был 401 - отправляет запрос на обновление токена на основании cookie
    this.api.interceptors.response.use(
      (res) => {
        const url = res.config.url
        const path = getPathFromUrl(url);

        if (!publicUrl.some(url => path.includes(url)) && !exactPublicUrl.find(el => el == removeQueryString(url))) {
          const token = localStorage.getItem("userToken");

          if (!token && !res.data.token) {
            return Promise.reject();
          }
        }

        return res.data || res.status === 204 ? res : Promise.reject();
      },

      function (error) {
        const originalRequest = error?.config;
        const requestPath = normalizePath(originalRequest?.url);


        // Если базовый URL был равен URL обновлению токена - возвращаем пустой объект ответа
        if (normalizePath(error?.config?.url) === normalizePath(`${API_BASE_URL}auth/refresh-token`)){
          return Promise.reject(AuthService.logout());
        }

        // if (error?.response?.status === 403) {
        //   return Promise.reject(AuthService.logout());
        // }

        if (!getInArr(error?.response?.status, [401])) {
          return handleMaxTwoFactorResponse({ api, error }).then((maxTwoFactorResponse) => {
            if (maxTwoFactorResponse) {
              return maxTwoFactorResponse;
            }

            if(
              error?.response?.data &&
              !error?.config?.skipGlobalErrorAlert &&
              !isMaxPlatformGateError(error)
            ){
              ErrorHandling.showAlert(error?.response?.data, {
                disableTextTruncate: Boolean(error?.config?.disableGlobalErrorTextTruncate),
              });
            }

            return error;
          });
        }

        if (requestPath === 'auth/login') {
          return error;
        }

        if (error?.response?.status === 401) {
          if (!originalRequest || originalRequest._retryAfterRefresh) {
            AuthService.logout();
            return Promise.reject(error);
          }

          originalRequest._retryAfterRefresh = true;

          if (isRefreshing) {
            if (failedQueue.length > 30) {
              failedQueue.forEach((promise) => promise.reject(promise.error));
              failedQueue = [];
              ErrorHandling.showAlert("Произошла ошибка. Пожалуйста, войдите снова.");
              AuthService.logout();
              return Promise.reject(error);
            }

            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject, config: originalRequest, error });
            });
          }

          isRefreshing = true;

          const retryRequest = (config, token) => api({
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: `Bearer ${token}`,
            },
          });

          return getAccessTokenAfterRefresh()
            .then((token) => {
              failedQueue.forEach((promise) => {
                promise.resolve(retryRequest(promise.config, token));
              });
              failedQueue = [];

              return retryRequest(originalRequest, token);
            })
            .catch((refreshError) => {
              failedQueue.forEach((promise) => promise.reject(promise.error || refreshError));
              failedQueue = [];
              AuthService.logout();
              return Promise.reject(error || refreshError);
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

         
      }
    );

    // Перехватчик запроса. Перед отправкой запроса проверяет время жизни JWT токена
    // Если времени жизни осталось меньше 30 секунд - отправляет запрос на обновление токена
    this.api.interceptors.request.use(async (config) => {
      const storedAccessToken = getStoredAccessToken();
      let token = storedAccessToken?.token || null;

      if (storedAccessToken) {
        const expTime = Number(storedAccessToken.payload?.exp) * 1000;
        const curTime = new Date().getTime();

        if (Number.isFinite(expTime) && (expTime - curTime <= 30000 || curTime > expTime)) {
          try {
            token = await getAccessTokenAfterRefresh();
          } catch (error) {
            AuthService.logout();
            return Promise.reject(error);
          }
        }
      }

      return {
        ...config,
        headers: {
          ...config.headers,
          withCredentials: true,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: true,
        baseURL: API_BASE_URL,
      };
    });
  }
}

export default new apiCreator().api;
